import classNames from 'classnames/bind';
import React, { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './button.css';
import { renderIcon } from './colored_icon';
import { Inline } from './layout';
import { LoadingDots } from './loading_dots';
import { Tooltip } from './tooltip';
import {
  TextRegular,
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  TextTiny,
  TextTinyLight,
} from './typography';

const cx = classNames.bind(styles);

export type VariantAndBackgroundProp =
  | {
      variant: 'primary';
      background?: 'black' | 'ai' | 'global' | 'negative';
      gradient?: true;
    }
  | {
      variant?: 'secondary' | undefined;
      background?:
        | 'white'
        | 'transparent'
        | 'grey3'
        | 'lightPurple'
        | 'lightGreen'
        | 'black';
      gradient?: undefined;
    }
  | {
      variant: 'tertiary';
      background?: 'transparent' | 'white' | 'grey2' | 'grey3';
      gradient?: undefined;
    }
  | {
      variant: 'subtle';
      background?: 'transparent' | 'grey2';
      gradient?: undefined;
    }
  | {
      variant: 'select';
      background?: undefined; // the select variant is special
      gradient?: undefined;
    };
const DEFAULT_BACKGROUND_FOR_VARIANT: Record<
  Exclude<VariantAndBackgroundProp['variant'], undefined>,
  VariantAndBackgroundProp['background']
> = {
  primary: 'black',
  secondary: 'white',
  tertiary: 'transparent',
  subtle: 'transparent',
  select: undefined,
};

export type ButtonProps = VariantAndBackgroundProp & {
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  weight?: 'light' | 'regular';
  checked?: boolean;
  disabled?: boolean;
  // TODO this should be the default - we need to test it doesn't break anything
  labelNoWrap?: boolean;
  labelNoPadding?: boolean;
  /** Disables the button without greying it out.  Does disable hover states */
  subtlyDisabled?: boolean;
  onClickWhenDisabled?: React.MouseEventHandler;
  loading?: boolean;
  fullWidth?: boolean;
  tooltip?: string;
  tooltipShortcut?: string;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  iconColor?: 'ai' | 'green' | 'lightPurple' | 'grey' | 'black';
  label?: string;
  subLabel?: string;
  shortcut?: string;
  icon?: React.ComponentType | React.ReactNode;
  iconRight?: React.ComponentType | React.ReactNode;
  children?: React.ReactNode;
  xalign?: 'left' | 'center';
} & Pick<
    React.ButtonHTMLAttributes<HTMLElement> &
      React.AnchorHTMLAttributes<HTMLElement>,
    | 'onClick'
    | 'type'
    | 'href'
    | 'target'
    | 'tabIndex'
    | 'autoFocus'
    | 'onMouseEnter'
    | 'onMouseLeave'
    | 'onContextMenu'
    | 'onMouseDown'
    | 'onWheel'
    | 'onFocus'
    | 'onBlur'
  >;

export type ButtonVariant = ButtonProps['variant'];
export type ButtonSize = ButtonProps['size'];

export const Button = React.forwardRef<HTMLElement | null, ButtonProps>(
  function _Button(
    {
      label,
      subLabel,
      labelNoWrap,
      labelNoPadding,
      shortcut,
      icon,
      iconRight,
      iconColor,
      children,
      variant = 'secondary',
      gradient = false,
      background = DEFAULT_BACKGROUND_FOR_VARIANT[variant],
      size = 'medium',
      weight = 'regular',
      xalign = 'center',
      checked,
      fullWidth,
      subtlyDisabled,
      disabled: obviouslyDisabled,
      loading,
      tooltip,
      tooltipShortcut,
      tooltipPlacement,
      onClick,
      onClickWhenDisabled,
      href,
      ...passthrough
    },
    propsRef,
  ) {
    const Tag = href ? 'a' : 'button';
    const [Regular, Small, Tiny] =
      weight === 'regular'
        ? [TextRegular, TextSmall, TextTiny]
        : [TextRegularLight, TextSmallLight, TextTinyLight];
    const Text = size === 'large' ? Regular : size === 'medium' ? Small : Tiny;

    if (children != null && typeof children === 'string') {
      console.warn(
        'Button was passed string children.  You probably want to pass a label prop instead, as label adds the typography wrapper.',
        { children },
      );
    }

    const ref = useRef<HTMLElement | null>(null);
    useEffect(() => {
      if (propsRef && typeof propsRef === 'function') {
        propsRef(ref.current);
      } else if (propsRef && typeof propsRef === 'object') {
        (propsRef as React.MutableRefObject<HTMLElement | null>).current =
          ref.current;
      }
    }, [propsRef]);

    return (
      <Tag
        // React types don't let you pass a HTMLAnchorElement as a HTMLElement here
        ref={ref as any}
        className={cx(
          styles.button,
          styles[variant],
          styles['background-' + background],
          size,
          xalign === 'left' && styles.xalignLeft,
          xalign === 'center' && styles.xalignCenter,
          label != null && !labelNoPadding && styles.hasLabelPadding,
          shortcut && iconRight == null && styles.endsWithShortcut,
          label == null && icon && iconRight == null && styles.onlyIcon,
          iconColor && styles['iconColor-' + iconColor],
          children != null && styles.hasCustomChildren,
          obviouslyDisabled && styles.isObviouslyDisabled,
          loading && styles.isLoading,
          checked && styles.isChecked,
          fullWidth && styles.isFullWidth,
          gradient && styles.gradient,
        )}
        aria-disabled={obviouslyDisabled || subtlyDisabled || loading}
        aria-checked={checked}
        onClick={
          obviouslyDisabled || subtlyDisabled
            ? onClickWhenDisabled || preventDefault
            : onClick
        }
        href={obviouslyDisabled || subtlyDisabled ? undefined : href}
        {...passthrough}
      >
        {icon && <div className={styles.icon}>{renderIcon(icon)}</div>}
        {label && (
          <div className={cx(styles.label, labelNoWrap && styles.noWrap)}>
            <Inline xalign="start" spacing={4}>
              <Text lineClamp={1}>{label}</Text>
              {subLabel && (
                <Text color="grey" lineClamp={1}>
                  {'• ' + subLabel}
                </Text>
              )}
            </Inline>
          </div>
        )}
        {shortcut && (
          <div className={styles.shortcut}>
            <TextTiny>{shortcut}</TextTiny>
          </div>
        )}
        {iconRight && (
          <div className={cx(styles.icon, styles.iconRight)}>
            {renderIcon(iconRight)}
          </div>
        )}
        {children}

        <CSSTransition
          in={loading}
          timeout={200}
          classNames={styles}
          unmountOnExit
        >
          <div className={styles.loadingDots}>
            <LoadingDots color="inherit" />
          </div>
        </CSSTransition>

        {tooltip !== undefined && (
          <Tooltip
            anchorRef={ref}
            label={tooltip}
            placement={tooltipPlacement}
            shortcut={tooltipShortcut}
          />
        )}
      </Tag>
    );
  },
);

function preventDefault(e: React.MouseEvent) {
  e.preventDefault();
}

export type Options<T> = readonly {
  label?: string;
  value: T;
  icon?: React.ComponentType | React.ReactNode;
  tooltip?: string;
  tooltipShortcut?: string;
}[];

export function RadioButtonGroup<T>({
  options,
  value,
  onChange,
  size = 'medium',
  disabled,
}: {
  options: Options<T>;
  value: T;
  onChange?: (value: T) => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}) {
  return (
    <RadioButtonGroupWrapper>
      {options.map((option, i) => (
        <Button
          key={i}
          onClick={() => onChange?.(option.value)}
          variant={option.value === value ? 'secondary' : 'subtle'}
          subtlyDisabled={option.value === value}
          label={option.label}
          icon={option.icon}
          tooltip={option.value === value ? undefined : option.tooltip}
          tooltipShortcut={option.tooltipShortcut}
          tooltipPlacement="vertical"
          size={size}
          disabled={disabled}
        />
      ))}
    </RadioButtonGroupWrapper>
  );
}

export function RadioButtonGroupWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className={styles.radioButtonGroup}>{children}</div>;
}

export function ButtonPalette({
  children,
  isDarker,
  isFullHeight,
}: {
  children: React.ReactNode;
  isDarker?: boolean;
  isFullHeight?: boolean;
}) {
  return (
    <div
      className={cx(
        styles.palette,
        isDarker && styles.isDarker,
        !isFullHeight && styles.reduceButtonHeights,
      )}
    >
      {children}
    </div>
  );
}

export function ButtonTextareaContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className={styles.textareaContainer}>{children}</div>;
}

export function SplitButtonContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className={styles.splitButtonContainer}>{children}</div>;
}
